// API de cobros / pagos.
// Fase 1: registro manual por transferencia.
// Fase 2 (más adelante): integración con Handy / MP Suscripciones.

import { supabase } from './supabase'

/** Trae todos los pagos con datos de cliente y entrega. */
export async function getPagos({ estado = null, desde = null, hasta = null } = {}) {
  let q = supabase
    .from('pagos')
    .select(`
      id, mes, monto, vence, estado, metodo, referencia_externa, notas,
      pagado_at, created_at,
      cliente:clientes!pagos_cliente_id_fkey(
        id, alias, barrio, direccion,
        titular:profiles!clientes_titular_fkey(id, nombre, phone, email)
      ),
      entrega:entregas!pagos_entrega_id_fkey(numero_orden, fecha_programada)
    `)
    .order('vence', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })

  if (estado) q = q.eq('estado', estado)
  if (desde) q = q.gte('vence', desde)
  if (hasta) q = q.lte('vence', hasta)

  const { data, error } = await q
  if (error) { console.error('getPagos:', error); return [] }
  return data || []
}

/** Trae pagos de UN cliente específico. */
export async function getPagosCliente(clienteId) {
  const { data, error } = await supabase
    .from('pagos')
    .select(`
      id, mes, monto, vence, estado, metodo, referencia_externa, notas,
      pagado_at, created_at,
      entrega:entregas!pagos_entrega_id_fkey(numero_orden, fecha_programada)
    `)
    .eq('cliente_id', clienteId)
    .order('vence', { ascending: false, nullsFirst: false })
  if (error) { console.error('getPagosCliente:', error); return [] }
  return data || []
}

/** Marca un pago como pagado (registro manual). */
export async function registrarPago(pagoId, { metodo = 'transferencia', referencia = null, notas = null } = {}) {
  const { data, error } = await supabase.rpc('registrar_pago', {
    pago_id: pagoId,
    metodo_param: metodo,
    referencia_param: referencia,
    notas_param: notas,
  })
  if (error) throw error
  return data
}

/** Crea un pago manual (retroactivo o adicional). */
export async function crearPagoManual({ clienteId, monto, mes, vence = null, metodo = 'transferencia', referencia = null, notas = null }) {
  const { data, error } = await supabase
    .from('pagos')
    .insert({
      cliente_id: clienteId,
      mes,
      vence: vence || mes,
      monto: Number(monto),
      metodo,
      referencia_externa: referencia,
      notas,
      estado: 'pendiente',
    })
    .select()
    .single()
  if (error) throw error
  return data
}

/** Cancela un pago (porque no corresponde, etc.). */
export async function cancelarPago(pagoId, motivo = null) {
  const { error } = await supabase
    .from('pagos')
    .update({
      estado: 'cancelado',
      notas: motivo ? `Cancelado: ${motivo}` : 'Cancelado',
    })
    .eq('id', pagoId)
  if (error) throw error
}

/** Dispara la generación automática de cobros del mensiversario para HOY (o fecha custom). */
export async function generarCobrosDelDia(fecha = null) {
  const { data, error } = await supabase.rpc('generar_cobros_del_dia', {
    fecha_param: fecha || new Date().toISOString().slice(0, 10),
  })
  if (error) throw error
  return data || []
}

/** Próximos cobros previstos por anniversary billing. */
export async function getCobrosProximos() {
  const { data, error } = await supabase
    .from('v_cobros_proximos')
    .select('*')
    .order('proximo_vence', { ascending: true })
  if (error) { console.error('getCobrosProximos:', error); return [] }
  return data || []
}

/** Lee la cuenta bancaria configurada (global). */
export async function getCuentaCobro() {
  const { data, error } = await supabase
    .from('app_config')
    .select('value')
    .eq('key', 'cuenta_cobro')
    .maybeSingle()
  if (error) return null
  try { return JSON.parse(data?.value || '{}') } catch { return null }
}

/** Guarda la cuenta bancaria global. */
export async function setCuentaCobro({ banco, numero, titular, alias }) {
  const value = JSON.stringify({ banco, numero, titular, alias })
  const { error } = await supabase
    .from('app_config')
    .update({ value })
    .eq('key', 'cuenta_cobro')
  if (error) throw error
}

/** Genera el texto del WhatsApp de cobro al cliente. */
export function textoWhatsAppCobro({ cliente, pago, cuenta }) {
  const nombre = cliente?.titular?.nombre?.split(' ')[0] || 'Hola'
  const monto = Number(pago.monto || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
  const venceLabel = pago.vence
    ? new Date(pago.vence + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
    : 'esta semana'

  const datosCuenta = cuenta?.banco
    ? `\n\n📌 Datos para transferir:\n${cuenta.banco}\nCuenta: ${cuenta.numero}\nTitular: ${cuenta.titular}${cuenta.alias ? `\nAlias: ${cuenta.alias}` : ''}`
    : ''

  return (
`Hola ${nombre}! 👋

Te paso el cobro de tu suscripción Limpack:

💰 *Monto:* $${monto}
📅 *Vence:* ${venceLabel}${datosCuenta}

Cuando hagas la transferencia, mandame el comprobante por acá y te doy el OK 🙂

¡Gracias por elegirnos!
— Limpack`
  )
}

/* ────── Capacidad de envío ────── */

export async function getCapacidadDiaria() {
  const { data } = await supabase.from('app_config').select('value').eq('key', 'capacidad_diaria').maybeSingle()
  return Number(data?.value || 20)
}

export async function setCapacidadDiaria(n) {
  const { error } = await supabase.from('app_config').update({ value: String(Math.max(1, parseInt(n) || 20)) }).eq('key', 'capacidad_diaria')
  if (error) throw error
}

export async function getCapacidadProximosDias() {
  const { data, error } = await supabase.from('v_capacidad_dias').select('*')
  if (error) return []
  // agrupar por fecha total
  const m = {}
  ;(data || []).forEach(r => {
    if (!m[r.fecha]) m[r.fecha] = { fecha: r.fecha, total: 0, barrios: [] }
    m[r.fecha].total += r.total
    m[r.fecha].barrios.push({ barrio: r.barrio, total: r.total })
  })
  return Object.values(m)
}

export const MODELO_COBRO_LABEL = {
  prepaid_mensiversario: '🔄 Mensiversario (cliente normal)',
  postpaid_entrega: '🤝 Al entregar (amigo/familia)',
  gratis: '🎁 Gratis (tester/regalo)',
}

export const ESTADO_PAGO_LABEL = {
  pendiente: 'Pendiente',
  pagado: 'Pagado',
  atrasado: 'Atrasado',
  cancelado: 'Cancelado',
}

export const ESTADO_PAGO_COLOR = {
  pendiente: { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  pagado:    { bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
  atrasado:  { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
  cancelado: { bg: '#F1F5F9', text: '#475569', dot: '#64748B' },
}
